import React from 'react';
import { useSite } from '../../context/SiteContext';
import { Input } from '../ui/input';
import { toast } from 'sonner';

export function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-xs font-semibold text-zinc-300 mb-1.5 block">{label}</label>
      {children}
      {hint && <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}

export function Card({ title, desc, children, footer }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 mb-5">
      <div className="px-5 py-4 border-b border-zinc-800">
        <h3 className="text-zinc-100 font-bold text-base">{title}</h3>
        {desc && <p className="text-zinc-500 text-xs mt-0.5">{desc}</p>}
      </div>
      <div className="p-5">{children}</div>
      {footer && <div className="px-5 py-3 border-t border-zinc-800 bg-zinc-900/40 flex justify-end gap-2">{footer}</div>}
    </div>
  );
}

export function SaveButton({ onClick, children = 'Save Changes' }) {
  return (
    <button onClick={onClick} className="px-4 h-9 rounded-md bg-[color:var(--gold)] hover:bg-[color:var(--gold)]/90 text-black text-sm font-bold">
      {children}
    </button>
  );
}

/**
 * Read-only display of an auto-generated anchor link for a content item
 * (product / category / platform / service / showcase tab). Lets the admin
 * one-click copy the anchor (e.g. `#product-cricket-pro`) to share / link to
 * that specific item on the landing page.
 */
export function AnchorField({ anchor, label = 'Anchor link' }) {
  if (!anchor) return null;
  const fragment = `#${anchor}`;
  return (
    <div className="mt-1.5">
      <label className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 block mb-1">{label}</label>
      <div className="flex items-center gap-1.5">
        <input
          readOnly
          value={fragment}
          onFocus={(e) => e.target.select()}
          className="flex-1 h-7 px-2 rounded bg-zinc-900 border border-zinc-800 text-[color:var(--gold)] text-[11px] font-mono"
          data-testid={`anchor-${anchor}`}
        />
        <button
          type="button"
          onClick={() => { navigator.clipboard.writeText(fragment); toast.success('Anchor copied'); }}
          className="h-7 px-2 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-[11px] font-bold"
        >Copy</button>
      </div>
    </div>
  );
}

// Helper to sanitize any CSS color or gradient string into a valid 7-character hex for HTML5 input[type=color]
function toSafeHex(val, fallback = '#000000') {
  if (!val || typeof val !== 'string') return fallback;
  const trimmed = val.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) return trimmed;
  if (/^#[0-9a-fA-F]{3}$/.test(trimmed)) {
    return `#${trimmed[1]}${trimmed[1]}${trimmed[2]}${trimmed[2]}${trimmed[3]}${trimmed[3]}`;
  }
  const match = trimmed.match(/#([0-9a-fA-F]{6})/);
  if (match) return `#${match[1]}`;
  return fallback;
}

export function ColorInput({ value, onChange, placeholder = '#000000' }) {
  const safeHex = toSafeHex(value, '#000000');
  return (
    <div className="flex items-center gap-2">
      <div className="relative shrink-0">
        <input
          type="color"
          value={safeHex}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-10 rounded border border-zinc-800 bg-zinc-900 cursor-pointer p-0.5"
          title="Choose color"
        />
      </div>
      <Input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-10 bg-zinc-900 border-zinc-800 text-zinc-100 font-mono text-sm flex-1"
      />
      <div
        className="w-10 h-10 rounded border border-zinc-800 shrink-0 shadow-inner"
        style={{ background: value || '#000000' }}
        title="Live color preview"
      />
    </div>
  );
}

/**
 * CSS-aware color field — value may be a plain hex/rgb OR any CSS background
 * (gradient, multi-stop, etc.). Always shows a native color picker that injects
 * its result into the text input.
 */
export function CssColorField({ value = '', onChange, placeholder = '' }) {
  const safeHex = toSafeHex(value, '#000000');

  const handlePicker = (hex) => {
    const v = (value || '').trim();
    if (!v || /^#[0-9a-fA-F]{3,8}$/.test(v) || !v.includes('gradient')) {
      onChange(hex);
    } else if (/#([0-9a-fA-F]{6})/.test(v)) {
      onChange(v.replace(/#([0-9a-fA-F]{6})/, hex));
    } else {
      onChange(hex);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <div className="relative shrink-0">
        <input
          type="color"
          value={safeHex}
          onChange={(e) => handlePicker(e.target.value)}
          className="w-10 h-10 rounded border border-zinc-800 bg-zinc-900 cursor-pointer p-0.5"
          title="Pick color"
        />
      </div>
      <Input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-10 bg-zinc-900 border-zinc-800 text-zinc-100 font-mono text-xs flex-1"
      />
      <div
        className="w-10 h-10 rounded border border-zinc-800 shrink-0 shadow-inner"
        style={{ background: value || 'transparent' }}
        title="Live background preview"
      />
    </div>
  );
}

export function TextField({ value, onChange, ...rest }) {
  return <Input value={value} onChange={(e) => onChange(e.target.value)} className="h-10 bg-zinc-900 border-zinc-800 text-zinc-100" {...rest} />;
}

export function useSection(key) {
  const { data, updateSection } = useSite();
  const [local, setLocal] = React.useState(data[key]);
  const [saving, setSaving] = React.useState(false);
  // Only re-sync local from incoming data when the SERIALIZED value has truly
  // changed. Reference-equal updates from our 3s polling no longer wipe out the
  // admin's unsaved edits (the "Link field not accepting input" bug).
  const lastSyncedRef = React.useRef(JSON.stringify(data[key] ?? null));
  React.useEffect(() => {
    const incoming = JSON.stringify(data[key] ?? null);
    if (incoming !== lastSyncedRef.current) {
      lastSyncedRef.current = incoming;
      setLocal(data[key]);
    }
  }, [data, key]);
  const save = async () => {
    setSaving(true);
    try {
      await updateSection(key, local);
      // Mark current local as the new baseline so the next poll doesn't revert us.
      lastSyncedRef.current = JSON.stringify(local ?? null);
      toast.success('Saved successfully');
    } catch (e) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };
  return [local, setLocal, save, saving];
}
